declare global {
  interface Window {
    initedZAFClient: any;
  }
}

export interface ZendeskUser {
  readonly id: number;
  readonly email: string;
  readonly prerenderId?: number;
  readonly integrationType?: string;
}

export interface ZendeskTicket {
  readonly id: number;
  readonly requester: ZendeskUser;
}

interface ZendeskGetUserResponse {
  readonly user: {
    readonly user_fields: {
      readonly prerender_id?: number;
      readonly integration_type?: string;
    };
  };
}

interface ZendeskTicketResponse {
  readonly error: any;
  readonly ticket: {
    readonly id: number;
    readonly requester: {
      readonly id: number;
      readonly email: string;
    };
  };
}

export class ZendeskError extends Error {}

export const getZendeskTicket = async (): Promise<ZendeskTicket | null> => {
  if (!window.initedZAFClient) {
    throw new ZendeskError();
  }

  const response = (await window.initedZAFClient.get("ticket")) as ZendeskTicketResponse;

  // despite their documentation the custom fields are not included in the ticket.requester field...
  const url = "/api/v2/users/" + response.ticket.requester.id + ".json";
  const user: ZendeskGetUserResponse = (await window.initedZAFClient.request(url)) as ZendeskGetUserResponse;

  return {
    id: response.ticket.id,
    requester: {
      id: response.ticket.requester.id,
      email: response.ticket.requester.email,
      prerenderId: user.user.user_fields.prerender_id,
      integrationType: user.user.user_fields.integration_type
    }
  };
};

export interface ZendeskUpdateUserData {
  readonly customFields?: {
    readonly prerenderId?: number;
    readonly integrationType?: string;
  };
}

interface ZendeskUpdateUserRequest {
  user: {
    user_fields?: {
      prerender_id?: number;
      integration_type?: string;
    };
  };
}

export const updateZendeskUser = async (zendeskUserId: number, update: ZendeskUpdateUserData): Promise<void> => {
  if (!window.initedZAFClient) {
    throw new ZendeskError();
  }

  const data: ZendeskUpdateUserRequest = {user: {}};
  if (update.customFields) {
    data.user.user_fields = {};
    if (update.customFields.prerenderId) data.user.user_fields.prerender_id = update.customFields.prerenderId;
    if (update.customFields.integrationType) data.user.user_fields.integration_type = update.customFields.integrationType;
  }

  if (Object.keys(data.user).length === 0) return;

  const url = "/api/v2/users/" + zendeskUserId + ".json";
  await window.initedZAFClient.request({url, type: "PUT", contentType: "application/json", data: JSON.stringify(data)});
};

enum UserFieldType {
  dropdown = "dropdown",
  integer = "integer"
}

interface UserFieldsResponse {
  readonly user_fields: [
    {
      readonly id: number;
      readonly type: UserFieldType;
      readonly title: string;
      readonly active: boolean;
      readonly custom_field_options?: [
        {
          readonly id: number;
          readonly name: string;
          readonly raw_name: string;
          readonly value: string;
        }
      ];
    }
  ];
}

export interface ZendeskUserField {
  readonly id: number;
  readonly type: UserFieldType;
  readonly title: string;
  readonly active: boolean;
  readonly options?: [
    {
      readonly id: number;
      readonly value: string;
    }
  ];
}

export const getZendeskUserFields = async (): Promise<Array<ZendeskUserField>> => {
  const url = "/api/v2/user_fields";
  const result: UserFieldsResponse = await window.initedZAFClient.request(url);

  return result.user_fields.map((userField) => ({
    id: userField.id,
    type: userField.type,
    title: userField.title,
    active: userField.active,
    options: userField.custom_field_options
  }));
};
